import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  ordersCreateTrackingValidationMutation,
  type CarrierRoot,
  type TrackingValidationResponse,
} from '@repo/sdk'

/** Outcome of a pre-save tracking validation: a provider result, or the request itself failed. */
export type TrackingValidationOutcome =
  | { kind: 'result'; result: TrackingValidationResponse }
  | { kind: 'request_failed' }

export type TrackingValidationAlertType = 'success' | 'warning' | 'error'

export function getTrackingValidationAlertType(
  status: TrackingValidationResponse['status'],
): TrackingValidationAlertType {
  if (status === 'VALID') return 'success'
  if (status === 'NOT_FOUND') return 'warning'
  return 'error'
}

/**
 * Shared SweetTracker pre-check used by the ship and change-tracking modals.
 * Keeps the latest result for the inline alert and exposes `validate` for save-time re-checks.
 */
export function useTrackingValidation() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [validationResult, setValidationResult] = useState<TrackingValidationResponse | null>(null)
  const mutation = useMutation(ordersCreateTrackingValidationMutation())

  const validate = async (
    carrier: CarrierRoot,
    trackingNumber: string,
  ): Promise<TrackingValidationOutcome> => {
    try {
      const result = await mutation.mutateAsync({
        body: { carrier, trackingNumber: trackingNumber.trim() },
      })
      setValidationResult(result)
      return { kind: 'result', result }
    } catch {
      setValidationResult(null)
      message.error(t('pages.orders.trackingValidation.requestFailed'))
      return { kind: 'request_failed' }
    }
  }

  const resetValidation = () => setValidationResult(null)

  const statusMessage = (status: TrackingValidationResponse['status']) =>
    t(`pages.orders.trackingValidation.status.${status}`)

  return {
    validationResult,
    resetValidation,
    validate,
    isValidating: mutation.isPending,
    statusMessage,
  }
}

/**
 * Decide how a save should proceed after validation.
 * - VALID → proceed without extra warning
 * - UNSUPPORTED_CARRIER → block
 * - anything else (NOT_FOUND, NOT_CONFIGURED, PROVIDER_ERROR, request failure) → ask "proceed anyway?"
 */
export function resolveValidationGate(
  outcome: TrackingValidationOutcome,
  statusMessage: (status: TrackingValidationResponse['status']) => string,
  requestFailedMessage: string,
): { action: 'proceed' } | { action: 'block' } | { action: 'confirm'; warning: string } {
  if (outcome.kind === 'request_failed') {
    return { action: 'confirm', warning: requestFailedMessage }
  }
  const { status } = outcome.result
  if (status === 'VALID') return { action: 'proceed' }
  if (status === 'UNSUPPORTED_CARRIER') return { action: 'block' }
  return { action: 'confirm', warning: statusMessage(status) }
}
